import React from "react";
import PropTypes from "prop-types";
import { VictoryChart, VictoryGroup, VictoryBar, VictoryAxis,
	VictoryLegend, VictoryLabel, VictoryPortal } from "victory";
import { injectIntl, intlShape } from "react-intl";
import { assign } from "lodash";

import AlmigTheme, { almigOrange, almigBlue } from "../grundelemente/AlmigTheme";
import { compressorComponentToI18nKey } from "../model/Kompressor";
import { getBarParams } from "../analysen/AnalyseTools";

import { sortWartungen } from "./SortWartungen";

const compressorComponent = (component, intl) => {
	let id = compressorComponentToI18nKey(component);
	if (!id) {
		return "";
	}
	return intl.formatMessage({ id });
};

const Label = props => {
	let { x, y, text, textAnchor, min, max, ...otherProps } = props;
	let value = text(props.datum);
	if (value === null) {
		return <div/>;
	}
	/* Mal 9 ist eine gute Näherung (zumindest für den Entwickler-Browser) */
	const width = value.toString().length * 9;
	const height = 16;

	x = textAnchor === "start"
		? props.scale.y(-min) + 3
		: textAnchor === "center"
			? props.scale.y(props.datum.y) + 3
			: props.scale.y(max) - 3 - width;
	y -= height/2;
	
	return <g transform={`translate(${x}, ${y})`} width={width} height={height}>
		<rect x={0} y={0} width={width} height={height} fill="white" />
		<VictoryLabel x={width/2} y={height/2} text={value} textAnchor="middle" verticalAnchor="center"
			{...otherProps} style={{
				fill: "#000000"
			}}/>
	</g>;
};

Label.propTypes = {
	x: PropTypes.number,			/* von Victory */
	y: PropTypes.number,			/* von Victory */
	scale: PropTypes.object,		/* von Victory */
	datum: PropTypes.object,		/* von Victory */
	text: PropTypes.func,			/* von Victory */
	textAnchor: PropTypes.string,	/* von Victory */
	min: PropTypes.number,			/* von hier */
	max: PropTypes.number			/* von hier */
};

const VerticalLine = props => {
	const { xDomain, scale, domain } = props;

	const x = scale.x(xDomain);
	const y1 = scale.y(domain.y[0]);
	const y2 = scale.y(domain.y[1]);

	return <line x1={x} y1={y1} x2={x} y2={y2} stroke="black" />;
};

VerticalLine.propTypes = {
	xDomain: PropTypes.number,	/* von hier */
	scale: PropTypes.object,	/* von Victory */
	domain: PropTypes.object	/* von Victory */
};

const WartungLegend = props => {
	const { x, y, datum } = props;

	if (!datum.fill2) {
		return <rect x={x-5} y={y-5} width={10} height={10} fill={datum.fill1} />;
	}
	return <g>
		<path fill={datum.fill1} d={`M ${x-5} ${y+5} v -10 h 10 Z`} />;
		<path fill={datum.fill2} d={`M ${x-5} ${y+5} L ${x+5} ${y-5} v 10 Z`} />;
	</g>;
};

WartungLegend.propTypes = {
	x: PropTypes.number,
	y: PropTypes.number,
	datum: PropTypes.object
};

const KompressorWartungChart = props => {
	const { data, compressor, intl } = props;

	/* Mit Array.prototype.filter werden leider die Referenzen auf
	 * die Wartungseinträge nicht kopiert, sondern übernommen. Die
	 * weiter unten folgenden Änderungen wirken sich dann auf die
	 * Originaldaten aus, was nicht im Sinne des Erfinders ist.
	 */
	let filtered = [];
	let x_max = 0;
	data.forEach(m => {
		if (m.cindex === compressor.cindex &&
			m.interval_h > 0) {
			filtered.push(assign({}, m));
			x_max = Math.max(x_max, m.interval_h);
		}
	});
	if (!filtered.length) {
		/* Victory verursacht einige Warnungen ohne Daten */
		return <div />;
	}
	/* Wie bei der HE die Skalierung im positiven Bereich machen */
	if (x_max > 4000) {
		x_max = 8000;
	} else if (x_max > 2000) {
		x_max = 4000;
	} else {
		x_max = 2000;
	}
	filtered.sort(sortWartungen);
	/* Spezialbehandlung für negative "remaining" Werte: diese sollen in einer anderen
	 * Farbe dargestellt werden. Am einfachsten geht das mit einer dritten Bar, die auf
	 * ein neues Datenfeld "neg_remaining_h" zugreift. Je nach Fall "remaining_h" > oder
	 * < 0 müssen dann nur noch "neg_remaining_h" und "remaining_h" so manipuliert werden,
	 * dass nur einer der beiden Balken tatsächlich angezeigt wird.
	 */
	let x_min = 0;
	filtered.forEach(m => {
		x_min = Math.min(x_min, m.remaining_h);
	});
	/* Aber auch nicht beliebig negative Werte darstellen */
	x_min = Math.max(x_min, -x_max / 8);
	/* x_max muss verschoben werden, falls x_min < 0 ist, und die dargestellten Balken müssen
	 * ebenso verschoben werden, weil Victory die Balken bei negativen Werten auf die linke
	 * Seite der Y-Achse zeichnet, und die Achsenbeschriftung dann weiter nach rechts wandert,
	 * und dann von den Balken übermalt wird -> nein, das wollen wir nicht!
	 */
	x_max -= x_min;
	filtered.forEach(m => {
		m.offset_h = -x_min;
		m.interval_d = m.interval_h - x_min;	/* "d" wie "display" */
		/* Für "remaining" nur einen von den zwei Balken einschalten: entweder den
		 * roten (remaining < 0), oder den orangen (remaining >= 0)
		 */
		if (m.remaining_h < 0) {
			m.neg_remaining_d = Math.max(m.remaining_h - x_min, 0);
			m.remaining_d = -x_min;
		} else {
			m.neg_remaining_d = -x_min;
			m.remaining_d = Math.max(m.remaining_h - x_min, 0);
		}
	});
	let pad_left = 100;
	let { bar_width, domain_padding } = getBarParams(filtered.length, pad_left, 1);
	return (
		<VictoryChart theme={AlmigTheme}
			domain={{ x: [0, x_max]}}
			domainPadding={{ x: [0, 0], y: domain_padding / 1.5 }}
			padding={{ left: pad_left, top: 20, bottom: 40 }}>
			<VictoryLegend x={pad_left} y={0} dataComponent={<WartungLegend />} data={[
				{ name: intl.formatMessage({id: "Interval"}), fill1: almigBlue },
				{ name: intl.formatMessage({id: "Time to next maintenance"}), fill1: almigOrange, fill2: "#ff0000" }
			]} />
			<VictoryAxis tickFormat={d => (d + x_min).toString()}
				style={assign({}, AlmigTheme.axis.style, {
					grid: { stroke: "lightgrey" }
				})}
				axisLabelComponent={<VictoryLabel angle={0} dy={8}/>}
				label={"t [" + intl.formatMessage({id: "h"}) + "]"} />
			<VictoryAxis dependentAxis tickValues={filtered.map(d => compressorComponent(d.component, intl))} />
			<VerticalLine xDomain={0 - x_min} />
			<VerticalLine xDomain={100 - x_min} />
			<VictoryGroup offset={0} style={{ data: { width: bar_width/1.5 }}}>
				<VictoryBar horizontal data={filtered} y="interval_d" y0="offset_h" color={almigBlue}
					x={d => d.component.toString()} labels={d => d.interval_h}
					labelComponent={<VictoryPortal>
						<Label textAnchor="end" min={x_min} max={x_max}/>
					</VictoryPortal>} />
				<VictoryBar horizontal data={filtered} y="remaining_d" y0="offset_h" color={almigOrange}
					x={d => d.component.toString()} labels={d => d.remaining_h < 0 ? null : d.remaining_h}
					labelComponent={<Label textAnchor="start" min={x_min} max={x_max}/>} />
				<VictoryBar horizontal data={filtered} y="neg_remaining_d" y0="offset_h" color="#ff0000"
					x={d => d.component.toString()} labels={d => d.remaining_h >= 0 ? null : d.remaining_h}
					labelComponent={<Label textAnchor="center" min={x_min} max={x_max}/>} />
			</VictoryGroup>
		</VictoryChart>);
};

KompressorWartungChart.propTypes = {
	data: PropTypes.arrayOf(PropTypes.object).isRequired,
	compressor: PropTypes.object.isRequired,
	intl: intlShape.isRequired
};

export default injectIntl(KompressorWartungChart);
